import { type FC, useContext } from 'react';
import { ArrowRight, Clock } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  heading: {
    id: 'nextStep.heading',
    defaultMessage: 'What to do now',
    description: 'Heading of the block stating the single action the farmer should take next.',
  },
  then: {
    id: 'nextStep.then',
    defaultMessage: 'Then I will',
    description: 'Label before what the agronomist does once the farmer has acted.',
  },
});

interface Option {
  label: string;
  message: string;
}

function readOptions(value: unknown): Option[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const label = str(record.label);
    const message = str(record.message);
    return label && message ? [{ label, message }] : [];
  });
}

export const NextStep: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const surface = useContext(A2uiSurfaceContext);

  const action = str(node.props.action);
  const because = optStr(node.props.because);
  const deadline = optStr(node.props.deadline);
  const then = optStr(node.props.then);
  const options = readOptions(node.props.options);

  /** The screen prints "Then I will" itself. The model still writes the phrase
   *  into the value often enough that leaving it would read "Then I will Then I
   *  will name the cause", so strip any leading restatement in any of the three
   *  languages before rendering. */
  const thenTail = then
    ?.replace(
      /^\s*(?:then\s*)?(?:i\s+will|i'll|मी|मैं)?\s*(?:करेन|करूंगा)?\s*[,:—-]?\s*/i,
      '',
    )
    .trim();

  if (!action) {
    return null;
  }

  return (
    <section className="w-full rounded-lg border-2 border-primary/30 bg-primary/[0.06] p-5">
      <h2 className="mb-2 text-sm font-semibold text-primary">
        {intl.formatMessage(messages.heading)}
      </h2>

      <p className="text-balance text-xl font-bold leading-snug tracking-tight text-foreground">
        {action}
      </p>

      {deadline ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Clock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0">{deadline}</span>
        </p>
      ) : null}

      {because ? (
        <p className="mt-3 max-w-[36rem] text-base leading-relaxed text-muted-foreground">
          {because}
        </p>
      ) : null}

      {options.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {options.map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => surface?.dispatch('sendIntent', { message: option.message })}
              style={{ touchAction: 'manipulation' }}
              className="rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground transition-[transform,border-color] duration-150 ease-out hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-95"
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}

      {thenTail ? (
        <p className="mt-4 flex items-start gap-2 border-t border-primary/20 pt-3 text-sm text-muted-foreground">
          <ArrowRight className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span className="min-w-0">
            <span className="font-semibold text-foreground">
              {intl.formatMessage(messages.then)}
            </span>{' '}
            {thenTail}
          </span>
        </p>
      ) : null}
    </section>
  );
};
